import { Booking, Quote } from '../types';
import { PlanningHistoryRow } from '../components/PlanningBoard/types';

export function buildPlanningHistoryRows(
  quotes: Quote[],
  bookings: Booking[],
  scheduledAssignments: Record<string, string>,
  validateCrewComposition: (crewIds: string[]) => { valid: boolean }
): PlanningHistoryRow[] {
  const charterRows = quotes.map((q: any) => {
    const legs = Array.isArray(q.legs) ? q.legs : [];
    const aircraftAssigned = !!q.tailNumber && q.tailNumber !== 'UNASSIGNED';
    const legsComplete = legs.length > 0 && legs.every((l: any) => !!l.from && !!l.to && Number(l.blockTimeMinutes || 0) > 0);
    const timesSet = legs.length > 0 && legs.every((l: any) => !!l.etd && !!l.eta);
    const crewAssigned = legs.length > 0 && legs.every((l: any) => validateCrewComposition(l.assignedCrew || []).valid);
    const manifestState = q.passengerManifest?.isIssued
      ? 'ISSUED'
      : q.passengerManifest?.passengerCount > 0
      ? 'SUBMITTED'
      : q.passengerManifest
      ? 'PENDING'
      : 'MISSING';
    const checklist = [
      { ok: aircraftAssigned, label: 'Aircraft not assigned', critical: true },
      { ok: legsComplete, label: 'Leg details incomplete', critical: true },
      { ok: timesSet, label: 'Times not set', critical: true },
      { ok: crewAssigned, label: 'Crew not assigned / invalid', critical: true },
      { ok: manifestState !== 'MISSING', label: 'Manifest not submitted', critical: false },
    ];
    const criticalMissing = checklist.filter(c => c.critical && !c.ok);
    const warningMissing = checklist.filter(c => !c.critical && !c.ok);
    const criticalChecksTotal = checklist.filter(c => c.critical).length;
    const criticalChecksCompleted = checklist.filter(c => c.critical && c.ok).length;
    const unplannedSeverity: 'CRITICAL' | 'WARNING' | 'OK' =
      criticalMissing.length > 0 ? 'CRITICAL' : warningMissing.length > 0 ? 'WARNING' : 'OK';
    return {
      id: q.id,
      source: 'CHARTER' as const,
      date: q.date,
      customerName: q.customerName || 'Client',
      bookingId: q.bookingId || '—',
      pnrCode: q.pnrCode || '—',
      quoteNumber: q.quoteNumber || '—',
      status: q.status || 'DRAFT',
      tailNumber: q.tailNumber || 'UNASSIGNED',
      pax: legs.reduce((sum: number, l: any) => sum + Number(l.paxCount || 0), 0),
      sectors: legs.length,
      manifestState,
      unplannedSeverity,
      missingItems: [...criticalMissing, ...warningMissing].map(item => item.label),
      criticalChecksCompleted,
      criticalChecksTotal,
    };
  });

  const scheduledRows = bookings.map((b: any) => {
    const assignedTail = scheduledAssignments[b.id] || 'UNASSIGNED';
    const checklist = [
      { ok: assignedTail !== 'UNASSIGNED', label: 'Aircraft not assigned', critical: true },
      { ok: !!b.from && !!b.to && Number(b.blockTimeMinutes || 0) > 0, label: 'Leg details incomplete', critical: true },
      { ok: !!b.etd && !!b.eta, label: 'Times not set', critical: true },
    ];
    const criticalMissing = checklist.filter(c => c.critical && !c.ok);
    const criticalChecksTotal = checklist.filter(c => c.critical).length;
    const criticalChecksCompleted = checklist.filter(c => c.critical && c.ok).length;
    const unplannedSeverity: 'CRITICAL' | 'WARNING' | 'OK' =
      criticalMissing.length > 0 ? 'CRITICAL' : 'OK';
    return {
      id: b.id,
      source: 'SCHEDULED' as const,
      date: b.date,
      customerName: b.customerName || 'Scheduled',
      bookingId: b.id || '—',
      pnrCode: b.pnrCode || '—',
      quoteNumber: '—',
      status: b.status || 'SCHEDULED',
      tailNumber: assignedTail,
      pax: Number(b.paxCount || 0),
      sectors: 1,
      manifestState: 'N/A',
      unplannedSeverity,
      missingItems: criticalMissing.map(item => item.label),
      criticalChecksCompleted,
      criticalChecksTotal,
    };
  });

  return [...charterRows, ...scheduledRows].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}
